import React, { FC } from 'react';
import classNames from 'classnames';

import { HRule } from 'Shared/components';
import { PaymentPlanType } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { useAppContext, usePurchaseContext } from 'QuoteApp/services/providers';
import { isInstallmentPlan, isPayInFullPlan } from 'QuoteApp/utils/paymentPlan';

import { FeeBreakdownSection, SummarySection } from './SidebarSections';
import { PaymentScheduleModal } from '../PaymentScheduleModal';
import { FlexiblePriceSummary } from './FlexiblePriceSummary';
import { buildDownPaymentFeeLines, buildInstallmentFeeLines } from './utils';
import styles from './styles.module.css';

type FlexiblePaymentSidebarProps = {
  paymentPlan: PaymentPlanType;
  excludedDrivers: number;
  includedDrivers: number;
};

export const FlexiblePaymentSidebar: FC<FlexiblePaymentSidebarProps> = ({
  paymentPlan,
  excludedDrivers,
  includedDrivers,
}) => {
  const {
    details,
    api,
    config,
    rate,
    rateLoading,
    detailsLoading,
    fetchRate,
    refreshDetails,
  } = useAppContext();
  const { getSignaturePacket } = usePurchaseContext();

  const installmentFeeMessage = paymentPlan?.totalAmountBreakdown
    ? null
    : paymentPlan?.messages.find(m => m.type === 'fee')?.description;

  const feeStrategy = details.paymentPreferences.variableFeeStrategySelected
    ? 'variable'
    : 'predictable';

  const hasFees = paymentPlan.paymentSchedule.some(schedule =>
    schedule.items?.some(item => item.type === 'FEE')
  );

  const hasSupportForPredictableFee = !!config?.accessEnabled;

  const buildUpdatePayloadForStrategy = (strategyName: string) => {
    return {
      paymentPreferences: {
        variableFeeStrategySelected: strategyName === 'variable',
      },
    };
  };

  const getAmountValue = (value: string | null | undefined): number => {
    if (!value) return 0;

    const numericPart = value.replace(/[^0-9.]/g, '');
    const amount = parseFloat(numericPart);

    return amount > 0 ? amount : 0;
  };

  return (
    <>
      <div className={classNames(styles.section, styles.dueToday)}>
        <FlexiblePriceSummary
          frequency={paymentPlan.paymentFrequencyIdentifier}
          frequencyLabel={paymentPlan.paymentFrequencyLabel}
          installmentAmount={getAmountValue(paymentPlan.installmentAmount)}
          downPayment={getAmountValue(paymentPlan.downPaymentAmount)}
          totalAmount={getAmountValue(paymentPlan.totalPremiumAmount)}
          dueToday={getAmountValue(paymentPlan.downPaymentAmount)}
        />
        {installmentFeeMessage && (
          <div className={styles.feeDisclaimer}>
            <span>*</span>
            <span>{installmentFeeMessage}</span>
          </div>
        )}
        <HRule size="sm" />
        {isInstallmentPlan(paymentPlan) && (
          <>
            <PaymentScheduleModal currentPlan={paymentPlan} />
            <HRule size="sm" />
          </>
        )}
      </div>

      {rate && hasFees && (
        <>
          <FeeBreakdownSection
            rate={rate}
            feeStrategy={feeStrategy}
            downPaymentFeeLines={buildDownPaymentFeeLines(paymentPlan)}
            installmentFeeLines={buildInstallmentFeeLines(paymentPlan)}
            isPayInFullPlan={isPayInFullPlan(paymentPlan)}
            hasSupportForPredictableFee={hasSupportForPredictableFee}
            isLoading={rateLoading || detailsLoading}
            onStrategySelect={strategyName => {
              api
                .saveAppData({
                  data: buildUpdatePayloadForStrategy(strategyName),
                })
                .then(async () => {
                  await fetchRate();
                  // Refresh details to reflect the latest data generated after rate
                  await refreshDetails();
                  await getSignaturePacket();
                });
            }}
          />
          <HRule size="sm" />
        </>
      )}

      <SummarySection
        productName="Auto"
        effectiveDate={details.effectiveDate.value}
        excludedDrivers={excludedDrivers}
        numberOfDrivers={includedDrivers}
        numberOfVehicles={Object.keys(details.vehicles).length}
      />
    </>
  );
};
